import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import CommandCenterService from '../services/CommandCenterService';
import PatientService from '../services/PatientService';
import Badge from '../components/ui/Badge';

// Standard START/MCI triage order — most critical first, so a screen glanced at from
// across a room shows what matters most without scrolling. Unrecognized/blank triage
// values sort last rather than crashing the sort.
const TRIAGE_ORDER = { Red: 0, Yellow: 1, Green: 2, Black: 3 };
const TRIAGE_BADGE = { Red: 'red', Yellow: 'amber', Green: 'green', Black: 'black' };
const TRIAGE_CARD_BORDER = {
  Red: 'border-red-500',
  Yellow: 'border-orange-400',
  Green: 'border-green-500',
  Black: 'border-neutral-300',
};
const TRIAGE_SUMMARY_STYLE = {
  Red: 'border-red-500/40 bg-red-500/10 text-red-400',
  Yellow: 'border-orange-400/40 bg-orange-400/10 text-orange-300',
  Green: 'border-green-500/40 bg-green-500/10 text-green-400',
  Black: 'border-neutral-400/40 bg-white/5 text-neutral-200',
};
const TRIAGE_KEYS = ['Red', 'Yellow', 'Green', 'Black'];

// Read-only, no-personal-data, large-format status board meant for a wall-mounted
// display (e.g. in an ER or command post) rather than for interaction — deliberately
// skips the Sidebar layout so it takes over the full viewport. Polls at the same
// 15s interval as PatientApp.jsx for consistency rather than wiring sockets, since
// this only ever needs to be "roughly live", not instant.
export default function PatientBoard() {
  const [incident, setIncident] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  const load = useCallback(async () => {
    try {
      const commandResponse = await CommandCenterService.getStats();
      const activeIncident = commandResponse?.data?.activeIncident || null;
      setIncident(activeIncident);
      if (activeIncident) {
        const { entity } = await PatientService.findByAnnouncement(activeIncident.id);
        setPatients(entity || []);
      } else {
        setPatients([]);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const dataInterval = setInterval(load, 15000);
    return () => clearInterval(dataInterval);
  }, [load]);

  useEffect(() => {
    const clockInterval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clockInterval);
  }, []);

  const sorted = [...patients].sort(
    (a, b) => (TRIAGE_ORDER[a.triage] ?? 9) - (TRIAGE_ORDER[b.triage] ?? 9)
  );
  const counts = patients.reduce((acc, p) => {
    acc[p.triage] = (acc[p.triage] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-ink-950 text-white p-8">
      <div className="flex items-start justify-between mb-8 gap-6">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/dashboard" className="text-neutral-500 hover:text-white transition-colors shrink-0" title="Back to dashboard">
            <ArrowLeft className="w-7 h-7" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-4xl font-bold truncate">{incident ? incident.title : 'No Active Incident'}</h1>
            <p className="text-neutral-400 text-lg mt-1">Patient Status Board</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-3xl font-mono tabular-nums">{now.toLocaleTimeString()}</div>
          <div className="text-neutral-400">
            {now.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {TRIAGE_KEYS.map((t) => (
          <div key={t} className={`rounded-xl p-6 text-center border ${TRIAGE_SUMMARY_STYLE[t]}`}>
            <div className="text-5xl font-bold tabular-nums">{counts[t] || 0}</div>
            <div className="text-lg uppercase tracking-wide mt-2">{t}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-2xl text-neutral-400 text-center py-24">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="text-2xl text-neutral-400 text-center py-24">
          {incident ? 'No patients registered for this incident yet.' : 'No active incident right now.'}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {sorted.map((p) => (
            <PatientCard key={p.recordId} patient={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function PatientCard({ patient }) {
  return (
    <div className={`rounded-2xl p-6 border-l-[6px] bg-ink-900 ${TRIAGE_CARD_BORDER[patient.triage] || 'border-neutral-600'}`}>
      <div className="flex items-center justify-between mb-4 gap-3">
        <span className="text-2xl font-bold truncate">{patient.id}</span>
        <Badge variant={TRIAGE_BADGE[patient.triage] || 'neutral'} className="text-sm px-3 py-1.5 shrink-0">
          {patient.triage || 'Unknown'}
        </Badge>
      </div>
      <div className="space-y-2 text-lg text-neutral-300">
        <div>
          Zone: <span className="text-white font-semibold">{patient.zone || '—'}</span>
        </div>
        <div>
          Vitals: <span className="text-white">{patient.vitals}</span>
        </div>
        <div>
          CBRNE: <span className="text-white">{patient.cbrne}</span>
        </div>
        <div>
          Decon: <span className="text-white">{patient.decon}</span>
        </div>
        <div>
          Destination: <span className="text-white font-semibold">{patient.destination}</span>
        </div>
      </div>
    </div>
  );
}
